import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import ReactECharts from 'echarts-for-react';
import { useUnits } from '../../contexts/UnitsContext';
import { lbsToKg } from '../../utils/units';
import { findCurrentWorkoutHistoryPoint, isCurrentWorkoutHistoryPoint, mergeCurrentWorkoutHistoryPoint } from './workoutExerciseHistoryState';
import { isStrengthHistoryCategory } from './workoutExerciseHistoryCategory';
import { formatCardioDistance, getCardioDistanceAxisLabel, shouldProjectWorkoutScore, toDisplayCardioDistance } from './workoutExerciseHistoryUnits';

const roundMaybe = (value, digits = 1) => {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const formatDateLabel = (dateLike) => {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDurationCompact = (seconds) => {
  if (!Number.isFinite(seconds)) return '';
  const safeSeconds = Math.max(0, Math.round(seconds));
  const h = Math.floor(safeSeconds / 3600);
  const m = Math.floor((safeSeconds % 3600) / 60);
  const s = safeSeconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const VOLUME_COLOR = '#a855f7'; // purple
const SCORE_COLOR = '#facc15'; // yellow — primary metric; crown sits here
const WEIGHT_COLOR = '#ec4899'; // pink
const STRETCH_TIME_COLOR = '#3b82f6'; // blue
const CALORIES_COLOR = '#f97316'; // orange

const RANGES = {
  MONTH: 30 * 24 * 60 * 60 * 1000,
  THREE_MONTHS: 90 * 24 * 60 * 60 * 1000,
  YEAR: 365 * 24 * 60 * 60 * 1000,
  ALL: 'all'
};

// Keep reward effects aligned with update animations.
const UPDATE_ANIMATION_MS = 350;
const INTRO_ANIMATION_MS = 450;

// Keep bar width from overpowering the selected volume datapoint marker.
const SELECTION_SYMBOL_SIZE_PX = 6;

const buildLeftAxisScale = (maxValue) => {
  const safeMax = Number.isFinite(maxValue) ? maxValue : 0;
  if (safeMax <= 0) {
    return { min: 0, max: 1, interval: 1, splitNumber: 4, labelValue: 0 };
  }

  // We want the maximum *data* value label at 25% height.
  // Achieve that by making axis max = 4 * maxValue and splitNumber=4,
  // which yields ticks at 0, maxValue, 2*maxValue, 3*maxValue, 4*maxValue.
  const axisMax = safeMax * 4;
  return {
    min: 0,
    max: axisMax,
    interval: safeMax,
    splitNumber: 4,
    labelValue: safeMax
  };
};

const buildLeftAxisScaleNoCompress = (maxValue) => {
  const safeMax = Number.isFinite(maxValue) ? maxValue : 0;
  if (safeMax <= 0) {
    return { min: 0, max: 1, interval: 1, splitNumber: 4, labelValue: 0 };
  }

  // When volume is not displayed (e.g., stretching), let the series use most
  // of the vertical space instead of pinning the max datapoint at ~25% height.
  const axisMax = Math.max(safeMax * 1.15, safeMax + 1e-9);
  return {
    min: 0,
    max: axisMax,
    // Ensure we always get a tick exactly at the max datapoint value.
    interval: safeMax,
    splitNumber: 1,
    labelValue: safeMax
  };
};

// Gentle pulse applied to the bar and circle marking the in-progress workout.
// ~33ms steps keep the per-step delta small enough that the bar and circle stay
// visually aligned even through ECharts transitions.
const PULSE_FRAME_MS = 33;
const PULSE_HZ = 0.55;

// Wall-clock driven so the wave is deterministic regardless of timer drift.
const pulseOpacityAt = (elapsedSeconds) =>
  0.4 + 0.6 * (0.5 + 0.5 * Math.sin(elapsedSeconds * 2 * Math.PI * PULSE_HZ));

const withPulsedCurrentBar = (leftSeries, currentPoint, opacity) => {
  if (!Array.isArray(leftSeries) || leftSeries.length === 0) return leftSeries;
  const hasLeft = leftSeries.some((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1]) && d.value[1] > 0);
  if (!hasLeft) return leftSeries;
  if (!currentPoint) return leftSeries;
  const currentX = currentPoint.t;
  if (!(typeof currentX === 'number' && Number.isFinite(currentX))) return leftSeries;

  return leftSeries.map((d) => {
    const x = d.value[0];
    const y = d.value[1];
    const matchesCurrent = (typeof x === 'number' && Number.isFinite(x) && Math.abs(x - currentX) < 1);
    if (!matchesCurrent) return d;
    if (!(typeof y === 'number' && Number.isFinite(y))) return d;

    return {
      value: [x, y],
      id: d.id,
      groupId: d.groupId,
      itemStyle: {
        color: `rgba(255,255,255,${opacity})`
      }
    };
  });
};

const pulsedMarkerItemStyle = (baseItemStyle, opacity) => ({
  ...baseItemStyle,
  borderColor: `rgba(255,255,255,${opacity})`,
  shadowColor: `rgba(255,255,255,${opacity * 0.55})`
});

function WorkoutExerciseHistoryChart({ workoutExerciseId, category, backendService, currentWorkoutId, currentWorkoutDate, projectedVolume, completedVolume, projectedScore, completedScore, completedDistance, completedDurationSeconds, completedCalories, completedRepetitions, allSetsComplete }) {
  const { weightUnit, distanceUnit } = useUnits();
  const chartRef = useRef(null);
  const introTimerRef = useRef(null);
  const prevCurrentVolumeRef = useRef(null);
  const volumeBumpTimerRef = useRef(null);
  const volumeBumpPulseIntervalRef = useRef(null);
  const volumeLabelAnimFrameRef = useRef(null);
  const volumeLabelAnimStartRef = useRef(null);
  const prevMaxVolumeRef = useRef(null);
  const currentVolumeLabelAnimFrameRef = useRef(null);
  const currentVolumeLabelAnimStartRef = useRef(null);
  const leftLabelAnimFrameRef = useRef(null);
  const leftLabelAnimStartRef = useRef(null);
  const prevMaxLeftRef = useRef(null);
  const stableLeftScaleRef = useRef(null);
  const [introDone, setIntroDone] = useState(false);
  const [volumeBumpKey, setVolumeBumpKey] = useState(0);
  const [showVolumeBump, setShowVolumeBump] = useState(false);
  const [volumeBumpPulseTick, setVolumeBumpPulseTick] = useState(0);
  const [displayedMaxVolume, setDisplayedMaxVolume] = useState(0);
  const [displayedCurrentVolume, setDisplayedCurrentVolume] = useState(0);
  const [displayedMaxLeft, setDisplayedMaxLeft] = useState(0);
  const [selectedRange, setSelectedRange] = useState(RANGES.THREE_MONTHS);

  // Hide tooltip when user releases touch
  const hideTooltip = useCallback(() => {
    const chart = chartRef.current?.getEchartsInstance?.();
    if (chart) {
      chart.dispatchAction({ type: 'hideTip' });
    }
  }, []);

  // Touch release listener - hide tooltip when finger lifts
  useEffect(() => {
    const chartContainer = chartRef.current?.ele;
    if (!chartContainer) return;

    const handleTouchEnd = () => {
      hideTooltip();
    };

    chartContainer.addEventListener('touchend', handleTouchEnd);
    chartContainer.addEventListener('touchcancel', handleTouchEnd);
    
    return () => {
      chartContainer.removeEventListener('touchend', handleTouchEnd);
      chartContainer.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [hideTooltip]);

  const { data, isLoading, isError } = useQuery(
    ['workoutExerciseHistory', workoutExerciseId],
    () => backendService.fetchWorkoutExerciseHistory(workoutExerciseId),
    {
      enabled: Boolean(workoutExerciseId),
      retry: (failureCount, error) => error?.response?.status !== 503 && failureCount < 3,
      refetchOnWindowFocus: false
    }
  );

  // Reset intro state on id change and cleanup timers.
  useEffect(() => {
    if (isLoading) {
      setIntroDone(false);
      return;
    }

    setIntroDone(false);
    if (introTimerRef.current) {
      clearTimeout(introTimerRef.current);
    }
    
    introTimerRef.current = setTimeout(() => {
      introTimerRef.current = null;
      setIntroDone(true);
    }, INTRO_ANIMATION_MS);

    return () => {
      if (introTimerRef.current) {
        clearTimeout(introTimerRef.current);
        introTimerRef.current = null;
      }
    };
  }, [workoutExerciseId, isLoading]);

  const history = useMemo(() => mergeCurrentWorkoutHistoryPoint(data?.history, {
    category,
    currentWorkoutId,
    currentWorkoutDate,
    completedVolume,
    completedScore,
    completedDistance,
    completedDurationSeconds,
    completedCalories,
    completedRepetitions,
  }), [data?.history, category, currentWorkoutId, currentWorkoutDate, completedVolume, completedScore, completedDistance, completedDurationSeconds, completedCalories, completedRepetitions]);

  const historyStats = useMemo(() => {
    const allPoints = history
      .map((row) => {
        const workoutDate = row.workoutDate;
        const t = workoutDate ? new Date(workoutDate).getTime() : NaN;
        if (!Number.isFinite(t)) return null;
        return {
          t,
          workoutId: row.workoutId ? String(row.workoutId) : null,
          workoutDate,
          avgWeight: typeof row.avgWeight === 'number' ? row.avgWeight : null,
          totalVolume: typeof row.totalVolume === 'number' ? row.totalVolume : null,
          totalScore: typeof row.totalScore === 'number' ? row.totalScore : null,
          totalRepetitions: typeof row.totalRepetitions === 'number' ? row.totalRepetitions : null,
          totalDistance: typeof row.totalDistance === 'number' ? row.totalDistance : null,
          totalDurationSeconds: typeof row.totalDurationSeconds === 'number' ? row.totalDurationSeconds : null,
          totalCalories: typeof row.totalCalories === 'number' ? row.totalCalories : null,
          totalEquivalentVolume: typeof row.totalEquivalentVolume === 'number' ? row.totalEquivalentVolume : null,
          totalEquivalentScore: typeof row.totalEquivalentScore === 'number' ? row.totalEquivalentScore : null
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.t - b.t);

    const currentWorkoutDateMs = currentWorkoutDate ? new Date(currentWorkoutDate).getTime() : NaN;
    const anchorDate = Number.isFinite(currentWorkoutDateMs) 
      ? currentWorkoutDateMs 
      : (allPoints.length > 0 ? allPoints[allPoints.length - 1].t : Date.now());

    const totalHistoryDuration = anchorDate - (allPoints.length > 0 ? allPoints[0].t : anchorDate);
    
    return { allPoints, anchorDate, totalHistoryDuration, hasAnyHistory: allPoints.length > 0 };
  }, [history, currentWorkoutDate]);

  useEffect(() => {
    if (!historyStats.hasAnyHistory) return;
    
    const duration = historyStats.totalHistoryDuration;
    
    if (selectedRange === RANGES.YEAR && duration <= RANGES.THREE_MONTHS) {
        if (duration > RANGES.MONTH) setSelectedRange(RANGES.THREE_MONTHS);
        else setSelectedRange(RANGES.MONTH);
    }
    else if (selectedRange === RANGES.THREE_MONTHS && duration <= RANGES.MONTH) {
        setSelectedRange(RANGES.MONTH);
    }
    else if (selectedRange === RANGES.ALL && duration <= RANGES.YEAR) {
         if (duration > RANGES.THREE_MONTHS) setSelectedRange(RANGES.YEAR);
         else if (duration > RANGES.MONTH) setSelectedRange(RANGES.THREE_MONTHS);
         else setSelectedRange(RANGES.MONTH);
    }
  }, [historyStats, selectedRange]);

  const isRangeValid = useMemo(() => {
    if (!historyStats.hasAnyHistory) return true;
    const duration = historyStats.totalHistoryDuration;
    
    if (selectedRange === RANGES.YEAR && duration <= RANGES.THREE_MONTHS) return false;
    if (selectedRange === RANGES.THREE_MONTHS && duration <= RANGES.MONTH) return false;
    if (selectedRange === RANGES.ALL && duration <= RANGES.YEAR) return false;
    
    return true;
  }, [historyStats, selectedRange]);

  const computed = useMemo(() => {
    const { allPoints: rawAllPoints, anchorDate } = historyStats;

    // Sort allPoints by date to ensure consistent ordering for animations and record detection.
    // This is critical because imported exercises might not be in chronological order in the DB.
    const allPoints = Array.isArray(rawAllPoints)
      ? [...rawAllPoints].sort((a, b) => (a.t || 0) - (b.t || 0))
      : [];

    const points = selectedRange === RANGES.ALL 
      ? allPoints 
      : allPoints.filter(p => p.t >= anchorDate - selectedRange);

    const categoryLower = String(category || '').trim().toLowerCase();

    const hasStrength = isStrengthHistoryCategory(categoryLower);
    const hasStretching = categoryLower === 'stretching';
    const hasSport = categoryLower === 'sport';
    const hasStatic = categoryLower === 'static';
    const hasCardio = categoryLower === 'cardio';
    const hasLoadMetrics = allPoints.some(p => (
      (typeof p.totalVolume === 'number' && Number.isFinite(p.totalVolume) && p.totalVolume > 0) ||
      (typeof p.totalScore === 'number' && Number.isFinite(p.totalScore) && p.totalScore > 0) ||
      (typeof p.avgWeight === 'number' && Number.isFinite(p.avgWeight) && p.avgWeight > 0)
    ));
    const hasRepetitionMetrics = allPoints.some(p => (
      typeof p.totalRepetitions === 'number' && Number.isFinite(p.totalRepetitions) && p.totalRepetitions > 0
    ));
    const usesRepetitionFallback = hasStrength && !hasLoadMetrics && hasRepetitionMetrics;

    const pointHasDisplayableData = (p) => {
      if (!p) return false;
      if (hasStrength || hasStatic) {
        return (
          (typeof p.totalVolume === 'number' && Number.isFinite(p.totalVolume) && p.totalVolume > 0) ||
          (typeof p.avgWeight === 'number' && Number.isFinite(p.avgWeight) && p.avgWeight > 0) ||
          (typeof p.totalRepetitions === 'number' && Number.isFinite(p.totalRepetitions) && p.totalRepetitions > 0)
        );
      }
      if (hasStretching) {
        return (
          typeof p.totalDurationSeconds === 'number' &&
          Number.isFinite(p.totalDurationSeconds) &&
          p.totalDurationSeconds > 0
        );
      }
      if (hasSport) {
        return (
          (typeof p.totalEquivalentVolume === 'number' && Number.isFinite(p.totalEquivalentVolume) && p.totalEquivalentVolume > 0) ||
          (typeof p.totalCalories === 'number' && Number.isFinite(p.totalCalories) && p.totalCalories > 0)
        );
      }
      if (hasCardio) {
        return (
          (typeof p.totalDistance === 'number' && Number.isFinite(p.totalDistance) && p.totalDistance > 0) ||
          (typeof p.totalDurationSeconds === 'number' && Number.isFinite(p.totalDurationSeconds) && p.totalDurationSeconds > 0)
        );
      }
      return false;
    };

    const volumeSeries = [];
    // Scores are canonical points, not physical load; only weight/volume convert.
    const scoreSeries = [];
    const leftSeries = [];

    // First pass: identify current workout point and check if there's prior history
    const currentWorkoutIdStr = currentWorkoutId ? String(currentWorkoutId) : null;
    const isCurrentWorkoutPoint = (p) => isCurrentWorkoutHistoryPoint(p, {
      currentWorkoutId: currentWorkoutIdStr,
      currentWorkoutDate,
    });
    
    const priorPointsWithData = points.filter((p) => !isCurrentWorkoutPoint(p) && pointHasDisplayableData(p));
    const hasPriorHistory = priorPointsWithData.length > 0;

    if (hasStrength || hasStatic) {
      // Volume dominates on right axis.
      // Use allPoints to ensure stable series for animation, but respect current-point hiding logic.
      allPoints.forEach((p) => {
        const isCurrent = isCurrentWorkoutPoint(p);

        const hasPointVolume = typeof p.totalVolume === 'number' && Number.isFinite(p.totalVolume) && p.totalVolume > 0;
        const hasPointRepetitions = typeof p.totalRepetitions === 'number' && Number.isFinite(p.totalRepetitions) && p.totalRepetitions > 0;
        
        // For current point, also check live completedVolume state (handles unchecked sets)
        const hasLiveVolume = isCurrent && typeof completedVolume === 'number' && completedVolume > 0;
        const hasLiveRepetitions = isCurrent && typeof completedRepetitions === 'number' && completedRepetitions > 0;
        const hasAnyVolume = hasPointVolume || hasLiveVolume || hasPointRepetitions || hasLiveRepetitions;
        
        // Skip current workout point entirely if:
        // - No prior history AND
        // - No saved volume AND no live completed volume
        if (isCurrent && !hasPriorHistory && !hasAnyVolume) {
          return;
        }
        
        const rawVolume = p.totalVolume;
        const displayVolume = (typeof rawVolume === 'number' && Number.isFinite(rawVolume))
          ? (weightUnit === 'kg' ? lbsToKg(rawVolume) : rawVolume)
          : null;
        
        const chartVolume = (isCurrent && !allSetsComplete) ? null : displayVolume;
        volumeSeries.push({ value: [p.t, chartVolume], id: `vol-${p.t}`, groupId: `vol-${p.t}` });

        const rawScore = p.totalScore;
        const hasLiveScore = isCurrent && typeof completedScore === 'number' && completedScore > 0;
        const liveScoreRaw = hasLiveScore ? completedScore : null;
        const effectiveScore = (isCurrent && !allSetsComplete && hasLiveScore)
          ? liveScoreRaw
          : rawScore;
        const displayScore = (typeof effectiveScore === 'number' && Number.isFinite(effectiveScore))
          ? effectiveScore
          : null;
        const chartScore = (isCurrent && !allSetsComplete && !hasLiveScore) ? null : displayScore;
        scoreSeries.push({ value: [p.t, chartScore], id: `score-${p.t}`, groupId: `score-${p.t}` });

        const weightValue = usesRepetitionFallback ? p.totalRepetitions : p.avgWeight;
        const displayWeight = weightValue === null
          ? null
          : (usesRepetitionFallback ? weightValue : (weightUnit === 'kg' ? lbsToKg(weightValue) : weightValue));
        
        // Hide the left bar (intensity) only when in-progress with no completed sets.
        // When allSetsComplete, the bar should stay visible (the backend point has real data).
        const leftValue = (isCurrent && !allSetsComplete && !hasLiveVolume && !hasLiveRepetitions) ? null : displayWeight;
        leftSeries.push({ value: [p.t, leftValue], id: `left-${p.t}`, groupId: `left-${p.t}` });
      });
    } else if (hasStretching) {
      allPoints.forEach((p) => {
        const isCurrent = isCurrentWorkoutPoint(p);
        const hasTime = typeof p.totalDurationSeconds === 'number' && Number.isFinite(p.totalDurationSeconds) && p.totalDurationSeconds > 0;
        
        // Skip current workout point if it has no time and there's no prior history
        if (isCurrent && !hasPriorHistory && !hasTime) {
          return;
        }
        
        leftSeries.push({ value: [p.t, p.totalDurationSeconds ?? null], id: `left-${p.t}`, groupId: `left-${p.t}` });
      });
    } else if (hasSport) {
      allPoints.forEach((p) => {
        const isCurrent = isCurrentWorkoutPoint(p);

        const hasPointVolume = typeof p.totalEquivalentVolume === 'number' && Number.isFinite(p.totalEquivalentVolume) && p.totalEquivalentVolume > 0;
        
        // For current point, also check live completedVolume state (handles unchecked sets)
        const hasLiveVolume = isCurrent && typeof completedVolume === 'number' && completedVolume > 0;
        const hasAnyVolume = hasPointVolume || hasLiveVolume;
        
        // Skip current workout point if it has no volume and there's no prior history
        if (isCurrent && !hasPriorHistory && !hasAnyVolume) {
          return;
        }
        
        const rawEqVol = p.totalEquivalentVolume;
        const displayEqVol = (typeof rawEqVol === 'number' && Number.isFinite(rawEqVol))
          ? (weightUnit === 'kg' ? lbsToKg(rawEqVol) : rawEqVol)
          : null;
        
        const chartVolume = (isCurrent && !allSetsComplete) ? null : displayEqVol;
        volumeSeries.push({ value: [p.t, chartVolume], id: `vol-${p.t}`, groupId: `vol-${p.t}` });

        const rawEqScore = p.totalEquivalentScore;
        const hasLiveScore = isCurrent && typeof completedScore === 'number' && completedScore > 0;
        const liveScoreRaw = hasLiveScore ? completedScore : null;
        const effectiveScore = (isCurrent && !allSetsComplete && hasLiveScore)
          ? liveScoreRaw
          : rawEqScore;
        const displayScore = (typeof effectiveScore === 'number' && Number.isFinite(effectiveScore))
          ? effectiveScore
          : null;
        const chartScore = (isCurrent && !allSetsComplete && !hasLiveScore) ? null : displayScore;
        scoreSeries.push({ value: [p.t, chartScore], id: `score-${p.t}`, groupId: `score-${p.t}` });

        const cal = p.totalCalories;
        // Hide calories bar only when in-progress with no completed sets.
        const leftValue = (isCurrent && !allSetsComplete && !hasLiveVolume) ? null : cal;
        leftSeries.push({ value: [p.t, leftValue], id: `left-${p.t}`, groupId: `left-${p.t}` });
      });
    } else if (hasCardio) {
      // Cardio: distance on left axis (bar), duration on right axis (volume)
      allPoints.forEach((p) => {
        const isCurrent = isCurrentWorkoutPoint(p);
        const hasDistance = typeof p.totalDistance === 'number' && Number.isFinite(p.totalDistance) && p.totalDistance > 0;
        const hasDuration = typeof p.totalDurationSeconds === 'number' && Number.isFinite(p.totalDurationSeconds) && p.totalDurationSeconds > 0;
        
        // Skip current workout point if it has no data and there's no prior history
        if (isCurrent && !hasPriorHistory && !hasDistance && !hasDuration) {
          return;
        }
        
        // Duration goes on right axis (volumeSeries) - shown as minutes for readability
        const durationMinutes = hasDuration ? p.totalDurationSeconds / 60 : null;
        const chartDuration = (isCurrent && !allSetsComplete) ? null : durationMinutes;
        volumeSeries.push({ value: [p.t, chartDuration], id: `vol-${p.t}`, groupId: `vol-${p.t}` });
        
        // Distance goes on left axis (leftSeries) - in km or miles based on unit preference
        const rawDistance = p.totalDistance;
        const displayDistance = toDisplayCardioDistance(rawDistance, distanceUnit);
        const leftValue = (isCurrent && !allSetsComplete && !hasDistance) ? null : displayDistance;
        leftSeries.push({ value: [p.t, leftValue], id: `left-${p.t}`, groupId: `left-${p.t}` });
      });
    }
    
    // Calculate max values based on VISIBLE points only, to ensure correct Y-axis scaling.
    const visibleTimestamps = new Set(points.map(p => p.t));
    const volumeValues = volumeSeries
      .filter(d => visibleTimestamps.has(d.value[0]))
      .map((d) => d.value[1])
      .filter((v) => typeof v === 'number' && Number.isFinite(v));
    const scoreValues = scoreSeries
      .filter(d => visibleTimestamps.has(d.value[0]))
      .map((d) => d.value[1])
      .filter((v) => typeof v === 'number' && Number.isFinite(v));
    const leftValues = leftSeries
      .filter(d => visibleTimestamps.has(d.value[0]))
      .map((d) => d.value[1])
      .filter((v) => typeof v === 'number' && Number.isFinite(v));

    const maxVolumeRaw = volumeValues.length ? Math.max(...volumeValues) : 0;
    const maxScoreRaw = scoreValues.length ? Math.max(...scoreValues) : 0;
    // The right Y axis hosts both lines plus the crown overlay (score-based).
    const maxVolume = Math.max(maxVolumeRaw, maxScoreRaw);
    const maxLeft = leftValues.length ? Math.max(...leftValues) : 0;

    const currentPoint = findCurrentWorkoutHistoryPoint(points, {
      currentWorkoutId,
      currentWorkoutDate,
    }, pointHasDisplayableData);

    const currentVolumeMarker = (() => {
      // White circle / overlays anchor on the SCORE line (which now drives the crown).
      // If incomplete, strict check against completedScore (non-warmup score)
      if (!allSetsComplete) {
         if (!completedScore || completedScore <= 0) return null;
      }
      
      if (!currentPoint) return null;

      const x = currentPoint.t;
      if (hasStrength || categoryLower === 'static') {
        const raw = (!allSetsComplete && completedScore > 0) ? completedScore : currentPoint.totalScore;
        
        const y = (typeof raw === 'number' && Number.isFinite(raw))
          ? raw
          : null;
        return (typeof y === 'number' && Number.isFinite(y) && y > 0) ? { x, y } : null;
      }
      if (categoryLower === 'sport') {
        const raw = (!allSetsComplete && completedScore > 0) ? completedScore : currentPoint.totalEquivalentScore;
        const y = (typeof raw === 'number' && Number.isFinite(raw))
          ? raw
          : null;
        return (typeof y === 'number' && Number.isFinite(y) && y > 0) ? { x, y } : null;
      }
      return null;
    })();

    // Raw current score marker (no unit conversion) for detecting true increases.
    const currentVolumeMarkerRaw = (() => {
      if (!currentPoint) return null;
      const x = currentPoint.t;
      if (hasStrength || categoryLower === 'static') {
        const raw = currentPoint.totalScore;
        const y = (typeof raw === 'number' && Number.isFinite(raw)) ? raw : null;
        return (typeof y === 'number' && Number.isFinite(y) && y > 0) ? { x, y } : null;
      }
      if (categoryLower === 'sport') {
        const raw = currentPoint.totalEquivalentScore;
        const y = (typeof raw === 'number' && Number.isFinite(raw)) ? raw : null;
        return (typeof y === 'number' && Number.isFinite(y) && y > 0) ? { x, y } : null;
      }
      return null;
    })();

    const projectedVolumePoint = (() => {
      // Projected ghost circle now lives on the score line.
      if (!shouldProjectWorkoutScore(categoryLower)) return null;
      if (!(typeof projectedScore === 'number' && Number.isFinite(projectedScore) && projectedScore > 0)) return null;

      const x = (() => {
        if (currentPoint && typeof currentPoint.t === 'number' && Number.isFinite(currentPoint.t)) {
          return currentPoint.t;
        }
        if (currentWorkoutDate) {
          const ms = new Date(currentWorkoutDate).getTime();
          if (Number.isFinite(ms)) {
            return ms;
          }
        }
        return null;
      })();

      if (!(typeof x === 'number' && Number.isFinite(x))) return null;
      return { x, y: projectedScore };
    })();

    const xCandidates = points
      .map((p) => p.t)
      .filter((t) => typeof t === 'number' && Number.isFinite(t));
    if (projectedVolumePoint && typeof projectedVolumePoint.x === 'number' && Number.isFinite(projectedVolumePoint.x)) {
      xCandidates.push(projectedVolumePoint.x);
    }

    const xMin = xCandidates.length ? Math.min(...xCandidates) : null;
    const xMax = xCandidates.length ? Math.max(...xCandidates) : null;

    // Helper: pick the score value for a point based on category. The crown
    // and PR markers track score (per the workout-score change), while the
    // volume line stays as a passive reference series.
    const getScoreValue = (p) => {
      if (!p) return null;
      if (hasStrength || categoryLower === 'static') {
        return typeof p.totalScore === 'number' && Number.isFinite(p.totalScore) ? p.totalScore : null;
      }
      if (categoryLower === 'sport') {
        return typeof p.totalEquivalentScore === 'number' && Number.isFinite(p.totalEquivalentScore) ? p.totalEquivalentScore : null;
      }
      return null;
    };

    // Calculate the global max score from all history (not just the visible window).
    const globalMaxRaw = (() => {
      let max = -Infinity;
      allPoints.forEach(p => {
         const val = getScoreValue(p);
         if (typeof val === 'number' && Number.isFinite(val) && val > max) {
             max = val;
         }
      });
      return max > -Infinity ? max : null;
    })();

    const globalMaxDisplay = (typeof globalMaxRaw === 'number' && Number.isFinite(globalMaxRaw))
      ? globalMaxRaw
      : 0;

    // Identify if the global max score point is present in the current visible window.
    const crownPoint = (() => {
        if (globalMaxRaw === null) return null;
        
        // Find the "official" global max point from allPoints (earliest occurrence).
        let officialGlobalMax = null;
        let firstValidPoint = null;

        for (const p of allPoints) {
             const val = getScoreValue(p);
             if (typeof val === 'number' && Number.isFinite(val) && val > 0) {
                 if (!firstValidPoint) firstValidPoint = p;

                 if (!officialGlobalMax || val > officialGlobalMax.val) {
                     officialGlobalMax = { p, val };
                 }
             }
        }
        
        if (!officialGlobalMax) return null;
        
        // Does the max occur on the very first workout ever? If so, no crown (per requirements).
        if (officialGlobalMax.p === firstValidPoint) return null;

        // Check if this specific point is in our visible `points` list.
        const isVisible = points.includes(officialGlobalMax.p);
        
        if (isVisible) {
             const t = officialGlobalMax.p.t;
             const raw = officialGlobalMax.val;
             return { x: t, y: raw };
        }
        return null;
    })();

    const maxVolumePoint = (() => {
      // Now tracks the SCORE line so that crown alignment / "current point is the max" logic
      // operates on the score series.
      if (!scoreSeries.length) return null;
      let best = null;
      for (const entry of scoreSeries) {
        const x = entry.value[0];
        const y = entry.value[1];
        if (!(typeof x === 'number' && Number.isFinite(x))) continue;
        if (!(typeof y === 'number' && Number.isFinite(y))) continue;

        if (!best) {
          best = { x, y };
          continue;
        }

        // Prefer the highest y; if tied, prefer the EARLIEST x (first time the max happened).
        if (y > best.y + 1e-9 || (Math.abs(y - best.y) < 1e-9 && x < best.x)) {
          best = { x, y };
        }
      }
      return best;
    })();

    const volumeRecordMarkers = (() => {
      // PR markers now track the score line; volume becomes a passive reference series.
      const records = [];
      let best = -Infinity;
      let firstPositiveSeen = false;
      const EPSILON = 1e-6;

      allPoints.forEach((p) => {
        const val = getScoreValue(p);
        if (!(typeof val === 'number' && Number.isFinite(val) && val > 0)) return;

        if (!firstPositiveSeen) {
          best = val;
          firstPositiveSeen = true;
          return;
        }

        if (val > best + EPSILON) {
          best = val;
          records.push({ x: p.t, y: val });
        }
      });

      // Filter to only show records that are in the current visible points.
      const visibleTimestamps = new Set(points.map(p => p.t));
      return records.filter(r => visibleTimestamps.has(r.x));
    })();

    const currentMarkerSeries = {
      id: 'current-marker',
      type: 'scatter',
      name: 'Current',
      yAxisIndex: currentVolumeMarker?.yAxisIndex ?? 1,
      data: currentVolumeMarker ? [[currentVolumeMarker.x, currentVolumeMarker.y]] : [],
      symbol: 'circle',
      symbolSize: currentVolumeMarker ? 12 : 0,
      silent: true,
      tooltip: { show: false },
      emphasis: { disabled: true },
      itemStyle: {
        color: 'rgba(0,0,0,0)',
        borderColor: '#ffffff',
        borderWidth: 2,
        shadowBlur: 10,
        shadowColor: 'rgba(255,255,255,0.55)'
      },
      z: 20
    };

    return {
      categoryLower,
      usesRepetitionFallback,
      points,
      volumeSeries,
      scoreSeries,
      leftSeries,
      maxVolume,
      maxLeft,
      currentPoint,
      currentVolumeMarker,
      currentMarkerSeries,
      currentVolumeMarkerRaw,
      projectedVolumePoint,
      maxVolumePoint,
      volumeRecordMarkers,
      crownPoint,
      globalMaxDisplay,
      xMin,
      xMax,
      totalHistoryDuration: historyStats.totalHistoryDuration,
      hasAnyHistory: historyStats.hasAnyHistory
    };
  }, [historyStats, category, weightUnit, distanceUnit, currentWorkoutId, currentWorkoutDate, completedVolume, completedRepetitions, projectedScore, completedScore, selectedRange, allSetsComplete]);

  // Pulse the in-progress workout's bar and circle. Routing the opacity through React
  // state rebuilt the whole chart option on every step, so patch just the two affected
  // series on the ECharts instance instead. rAF also parks the loop while backgrounded.
  useEffect(() => {
    const applyPulse = (opacity) => {
      const chart = chartRef.current?.getEchartsInstance?.();
      if (!chart || chart.isDisposed?.() || !computed) return;

      const series = [];
      const pulsedLeft = withPulsedCurrentBar(computed.leftSeries, computed.currentPoint, opacity);
      // Identity means there was nothing to tint, so leave the series untouched.
      if (pulsedLeft !== computed.leftSeries) {
        series.push({ id: 'left', data: pulsedLeft });
      }
      if (computed.currentMarkerSeries) {
        series.push({
          id: 'current-marker',
          itemStyle: pulsedMarkerItemStyle(computed.currentMarkerSeries.itemStyle, opacity)
        });
      }
      if (series.length === 0) return;

      chart.setOption({ series }, { lazyUpdate: false, silent: true });
    };

    if (!currentWorkoutId || allSetsComplete) {
      applyPulse(1);
      return;
    }

    const startTime = Date.now();
    let lastStep = 0;
    let frame = null;

    const tick = () => {
      frame = requestAnimationFrame(tick);
      const now = Date.now();
      if (now - lastStep < PULSE_FRAME_MS) return;
      lastStep = now;
      applyPulse(pulseOpacityAt((now - startTime) / 1000));
    };
    frame = requestAnimationFrame(tick);

    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [currentWorkoutId, allSetsComplete, computed]);

  // Animate the right-axis volume label value so it counts quickly to new values.
  // We keep the *tick position* anchored to the true maxVolume; only the displayed
  // label text changes during the animation.
  useEffect(() => {
    const globalRecord = (computed?.globalMaxDisplay && Number.isFinite(computed.globalMaxDisplay))
      ? computed.globalMaxDisplay
      : 0;

    // Prefer showing the projected volume (ghost yellow circle) if available.
    // Otherwise fallback to global max.
    const projectedY = (computed?.projectedVolumePoint && typeof computed.projectedVolumePoint.y === 'number' && Number.isFinite(computed.projectedVolumePoint.y))
      ? computed.projectedVolumePoint.y
      : 0;

    const target = (projectedY > 0) ? projectedY : globalRecord;

    if (!(target > 0)) {
      setDisplayedMaxVolume(0);
      prevMaxVolumeRef.current = 0;
      return;
    }

    prevMaxVolumeRef.current = target;

    // Cancel any in-flight animation.
    if (volumeLabelAnimFrameRef.current) {
      cancelAnimationFrame(volumeLabelAnimFrameRef.current);
      volumeLabelAnimFrameRef.current = null;
    }
    volumeLabelAnimStartRef.current = null;

    // Wait until the chart's intro animation is done before starting the count-up.
    // Also keep the label invisible while at 0.
    if (!introDone) {
      setDisplayedMaxVolume(0);
      return;
    }

    // Snap to 0 / identical values.
    setDisplayedMaxVolume((prev) => {
      if (!(target > 0)) return 0;
      if (!Number.isFinite(prev)) return Math.round(target);
      // If the target hasn't changed significantly (after rounding), keep the current value.
      if (prev === Math.round(target)) return prev;
      return prev;
    });

    const durationMs = 220;
    // Use the current state as start, unless it's invalid.
    // IMPORTANT: Do not default to target here, or we skip animation.
    // But if we are already at target (checked above), we won't animate anyway.
    const start = Number.isFinite(displayedMaxVolume) ? displayedMaxVolume : 0;
    const end = target;

    // If start is already effectively end (after rounding), do nothing.
    if (start === Math.round(end)) {
      setDisplayedMaxVolume(Math.round(end));
      return;
    }

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (ts) => {
      if (volumeLabelAnimStartRef.current === null) {
        volumeLabelAnimStartRef.current = ts;
      }
      const elapsed = ts - volumeLabelAnimStartRef.current;
      const p = Math.min(1, Math.max(0, elapsed / durationMs));
      const eased = easeOutCubic(p);
      const next = start + (end - start) * eased;
      setDisplayedMaxVolume(Math.round(next));

      if (p < 1) {
        volumeLabelAnimFrameRef.current = requestAnimationFrame(step);
      } else {
        volumeLabelAnimFrameRef.current = null;
        volumeLabelAnimStartRef.current = null;
        setDisplayedMaxVolume(Math.round(end));
      }
    };

    volumeLabelAnimFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (volumeLabelAnimFrameRef.current) {
        cancelAnimationFrame(volumeLabelAnimFrameRef.current);
        volumeLabelAnimFrameRef.current = null;
      }
      volumeLabelAnimStartRef.current = null;
    };
    // Intentionally do NOT depend on displayedMaxVolume to avoid restarting the animation mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed?.globalMaxDisplay, computed?.projectedVolumePoint, introDone]);

  // Animate the left-axis label value (avg weight / calories / stretch time) so it counts quickly
  // when the max changes, while keeping the tick position aligned to the true maxLeft.
  useEffect(() => {
    const target = (computed?.maxLeft && Number.isFinite(computed.maxLeft)) ? computed.maxLeft : 0;

    prevMaxLeftRef.current = target;

    if (leftLabelAnimFrameRef.current) {
      cancelAnimationFrame(leftLabelAnimFrameRef.current);
      leftLabelAnimFrameRef.current = null;
    }
    leftLabelAnimStartRef.current = null;

    // Wait until the chart's intro animation is done before starting the count-up.
    // Also keep the label invisible while at 0.
    if (!introDone) {
      setDisplayedMaxLeft(0);
      return;
    }

    setDisplayedMaxLeft((prev) => {
      if (!(target > 0)) return 0;
      if (!Number.isFinite(prev)) return target;
      if (Math.abs(prev - target) < 1e-6) return target;
      return prev;
    });

    if (!(target > 0)) return;

    const durationMs = 220;
    const start = (typeof displayedMaxLeft === 'number' && Number.isFinite(displayedMaxLeft)) ? displayedMaxLeft : target;
    const end = target;
    if (Math.abs(start - end) < 1e-6) {
      setDisplayedMaxLeft(end);
      return;
    }

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (leftLabelAnimStartRef.current === null) {
        leftLabelAnimStartRef.current = ts;
      }
      const elapsed = ts - leftLabelAnimStartRef.current;
      const p = Math.min(1, Math.max(0, elapsed / durationMs));
      const eased = easeOutCubic(p);
      const next = start + (end - start) * eased;
      setDisplayedMaxLeft(next);

      if (p < 1) {
        leftLabelAnimFrameRef.current = requestAnimationFrame(step);
      } else {
        leftLabelAnimFrameRef.current = null;
        leftLabelAnimStartRef.current = null;
        setDisplayedMaxLeft(end);
      }
    };

    leftLabelAnimFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (leftLabelAnimFrameRef.current) {
        cancelAnimationFrame(leftLabelAnimFrameRef.current);
        leftLabelAnimFrameRef.current = null;
      }
      leftLabelAnimStartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed?.maxLeft, introDone]);

  // When the current workout's volume datapoint increases, briefly show a pulsing ring + arrow.
  useEffect(() => {
    // Clear any pending timer on id change.
    if (volumeBumpTimerRef.current) {
      clearTimeout(volumeBumpTimerRef.current);
      volumeBumpTimerRef.current = null;
    }
    if (volumeBumpPulseIntervalRef.current) {
      clearInterval(volumeBumpPulseIntervalRef.current);
      volumeBumpPulseIntervalRef.current = null;
    }
    prevCurrentVolumeRef.current = null;
    setShowVolumeBump(false);
    setVolumeBumpPulseTick(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutExerciseId]);

  // Drive a quick "pulsing" feel while the bump is active.
  useEffect(() => {
    if (!showVolumeBump) {
      if (volumeBumpPulseIntervalRef.current) {
        clearInterval(volumeBumpPulseIntervalRef.current);
        volumeBumpPulseIntervalRef.current = null;
      }
      return;
    }

    setVolumeBumpPulseTick(0);
    volumeBumpPulseIntervalRef.current = setInterval(() => {
      setVolumeBumpPulseTick((t) => t + 1);
    }, 90);

    return () => {
      if (volumeBumpPulseIntervalRef.current) {
        clearInterval(volumeBumpPulseIntervalRef.current);
        volumeBumpPulseIntervalRef.current = null;
      }
    };
  }, [showVolumeBump, volumeBumpKey]);

  useEffect(() => {
    // Use raw (unconverted) values so unit toggles don't look like an increase.
    const y = computed?.currentVolumeMarkerRaw?.y;
    if (!(typeof y === 'number' && Number.isFinite(y))) {
      prevCurrentVolumeRef.current = null;
      return;
    }

    const prev = prevCurrentVolumeRef.current;
    prevCurrentVolumeRef.current = y;

    // Don't animate on first value.
    if (!(typeof prev === 'number' && Number.isFinite(prev))) return;

    if (y > prev + 1e-9) {
      setVolumeBumpKey((k) => k + 1);
      setShowVolumeBump(true);
      if (volumeBumpTimerRef.current) clearTimeout(volumeBumpTimerRef.current);
      // Only show while the datapoint is transitioning.
      volumeBumpTimerRef.current = setTimeout(() => {
        setShowVolumeBump(false);
        volumeBumpTimerRef.current = null;
      }, UPDATE_ANIMATION_MS);
    }
  }, [computed?.currentVolumeMarkerRaw?.x, computed?.currentVolumeMarkerRaw?.y]);

  // Animate the current workout volume label value (white axis-side label) so it counts up.
  useEffect(() => {
    const target = (computed?.currentVolumeMarker && typeof computed.currentVolumeMarker.y === 'number' && Number.isFinite(computed.currentVolumeMarker.y))
      ? computed.currentVolumeMarker.y
      : 0;

    // Cancel any in-flight animation.
    if (currentVolumeLabelAnimFrameRef.current) {
      cancelAnimationFrame(currentVolumeLabelAnimFrameRef.current);
      currentVolumeLabelAnimFrameRef.current = null;
    }
    currentVolumeLabelAnimStartRef.current = null;

    // Keep invisible while intro is playing or before any value exists.
    if (!introDone || !(target > 0)) {
      setDisplayedCurrentVolume(0);
      return;
    }

    // Snap identical values.
    setDisplayedCurrentVolume((prev) => {
      if (!Number.isFinite(prev)) return target;
      if (Math.abs(prev - target) < 1e-6) return target;
      return prev;
    });

    const durationMs = 220;
    const start = (typeof displayedCurrentVolume === 'number' && Number.isFinite(displayedCurrentVolume)) ? displayedCurrentVolume : target;
    const end = target;
    if (Math.abs(start - end) < 1e-6) {
      setDisplayedCurrentVolume(end);
      return;
    }

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (ts) => {
      if (currentVolumeLabelAnimStartRef.current === null) {
        currentVolumeLabelAnimStartRef.current = ts;
      }
      const elapsed = ts - currentVolumeLabelAnimStartRef.current;
      const p = Math.min(1, Math.max(0, elapsed / durationMs));
      const eased = easeOutCubic(p);
      const next = start + (end - start) * eased;
      setDisplayedCurrentVolume(next);

      if (p < 1) {
        currentVolumeLabelAnimFrameRef.current = requestAnimationFrame(step);
      } else {
        currentVolumeLabelAnimFrameRef.current = null;
        currentVolumeLabelAnimStartRef.current = null;
        setDisplayedCurrentVolume(end);
      }
    };

    currentVolumeLabelAnimFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (currentVolumeLabelAnimFrameRef.current) {
        cancelAnimationFrame(currentVolumeLabelAnimFrameRef.current);
        currentVolumeLabelAnimFrameRef.current = null;
      }
      currentVolumeLabelAnimStartRef.current = null;
    };
    // Intentionally do NOT depend on displayedCurrentVolume to avoid restarting mid-flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed?.currentVolumeMarker?.y, introDone]);

  const option = useMemo(() => {
    const {
      categoryLower,
      usesRepetitionFallback,
      points,
      volumeSeries,
      scoreSeries,
      leftSeries,
      maxVolume,
      maxLeft,
      currentPoint,
      currentVolumeMarker,
      currentMarkerSeries,
      projectedVolumePoint,
      maxVolumePoint,
      volumeRecordMarkers,
      crownPoint,
      globalMaxDisplay,
      xMin,
      xMax
    } = computed;

    const hasVolume = volumeSeries.some((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1]) && d.value[1] > 0);
    const hasLeft = leftSeries.some((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1]) && d.value[1] > 0);

    // Use compressed scale (bar at 25% height) when:
    // - There's visible volume history, OR
    // - There's a projected volume point (ghost circle), OR  
    // - There's completed volume in progress (even if not yet "banked" as a point)
    // This prevents the weight bar from taking full height on first exercise.
    const hasVolumeContext = hasVolume || 
      Boolean(projectedVolumePoint) || 
      (typeof completedVolume === 'number' && completedVolume > 0);
    
    // Prevent animation explosion: when bars disappear, ECharts simultaneously
    // animates bar values → 0 AND axis max 400 → 1. At the crossover frame
    // (e.g., bar=50, axisMax=50), the bar fills 100% height → "shoots to the roof".
    // Fix: hold the previous axis scale when maxLeft drops to 0 so bars exit within
    // a stable axis frame.
    const targetLeftScale = hasVolumeContext ? buildLeftAxisScale(maxLeft) : buildLeftAxisScaleNoCompress(maxLeft);
    const leftScale = (() => {
      if (maxLeft > 0) {
        stableLeftScaleRef.current = targetLeftScale;
        return targetLeftScale;
      }
      // Hold previous scale while bars animate out
      return stableLeftScaleRef.current || targetLeftScale;
    })();

    // Note: formatVolumeValue needs to reference categoryLower, which is available in computed
    const formatVolumeValue = (value) => {
      if (!Number.isFinite(value)) return '-';
      // For cardio, format as duration in minutes
      if (categoryLower === 'cardio') return String(roundMaybe(value, 1)) + 'm';
      return String(roundMaybe(value, 0));
    };

    // Figure out the current volume value (same y as the white circle marker when present).
    const currentVolumeY = (currentWorkoutId && currentVolumeMarker && typeof currentVolumeMarker.y === 'number' && Number.isFinite(currentVolumeMarker.y))
      ? currentVolumeMarker.y
      : null;

    const projectedVolumeY = (projectedVolumePoint && typeof projectedVolumePoint.y === 'number' && Number.isFinite(projectedVolumePoint.y) && projectedVolumePoint.y > 0)
      ? projectedVolumePoint.y
      : null;

    const labelVolumeValue = (projectedVolumeY && projectedVolumeY > 0)
      ? projectedVolumeY
      : (globalMaxDisplay > 0
        ? globalMaxDisplay
        // Cardio has no score-derived global max. Its right axis is duration,
        // so use the visible duration peak instead of falling back to 1-minute
        // ticks across the entire chart.
        : (categoryLower === 'cardio' && maxVolume > 0 ? maxVolume : 0));

    const suppressMaxVolumeLabel = (() => {
      if (!(labelVolumeValue > 0)) return false;
      if (!(typeof currentVolumeY === 'number' && Number.isFinite(currentVolumeY))) return false;
      if (!(currentVolumeY > 0)) return false;

      // Hide the yellow max label when the current volume is effectively equal to the label value.
      const eps = Math.max(1e-6, Math.abs(labelVolumeValue) * 1e-6);
      return Math.abs(currentVolumeY - labelVolumeValue) < eps;
    })();

    const peakVolume = Math.max(
      globalMaxDisplay > 0 ? globalMaxDisplay : 0,
      maxVolume > 0 ? maxVolume : 0,
      (typeof currentVolumeY === 'number' && Number.isFinite(currentVolumeY) && currentVolumeY > 0) ? currentVolumeY : 0,
      projectedVolumeY || 0
    );

    // Add headroom so the highest volume point (and reward effect) doesn't get clipped.
    // IMPORTANT: keep the right-axis tick aligned with the max datapoint by labeling
    // the tick at maxVolume (not the padded axis max).
    const volumeAxisMax = peakVolume > 0 ? Math.max(peakVolume * 1.3, peakVolume + 1) : 1;

    const showChart = hasVolume || hasLeft || Boolean(projectedVolumePoint);
    if (!showChart) {
      return null;
    }

    const isStrength = isStrengthHistoryCategory(categoryLower);
    const isStretching = categoryLower === 'stretching';
    const isSport = categoryLower === 'sport';
    const isStatic = categoryLower === 'static';
    const isCardio = categoryLower === 'cardio';

    const leftSeriesColor = (isStrength || isStatic)
      ? WEIGHT_COLOR
      : isStretching
        ? STRETCH_TIME_COLOR
        : isSport
          ? CALORIES_COLOR
          : isCardio
            ? '#22d3ee' // cyan for distance
            : 'rgba(255,255,255,0.85)';

    const leftName = usesRepetitionFallback
      ? 'Repetitions'
      : (isStrength || isStatic)
        ? `Avg Weight (${weightUnit === 'kg' ? 'kg' : 'lbs'})`
        : isStretching
          ? 'Total Time'
          : isSport
            ? 'Calories (kcal)'
            : isCardio
              ? getCardioDistanceAxisLabel(distanceUnit)
              : '';

    const volumeName = (isStrength || isStatic)
      ? `Volume (${weightUnit === 'kg' ? 'kg·reps' : 'lbs·reps'})`
      : isSport
        ? `Equivalent Volume (${weightUnit === 'kg' ? 'kg·reps' : 'lbs·reps'})`
        : isCardio
          ? 'Duration (min)'
          : `Volume (${weightUnit === 'kg' ? 'kg·reps' : 'lbs·reps'})`;

    const scoreName = (isStrength || isStatic || isSport) ? 'Score' : '';

    // Remove unused volumePointCount definition here as it's calculated later based on visibility
    // const volumePointCount = ... 

    const formatLeftValue = (value) => {
      if (!Number.isFinite(value)) return '-';
      if (isStretching) return formatDurationCompact(value);
      if (usesRepetitionFallback) return String(roundMaybe(value, 0));
      // Always show 1 decimal max for weight values (whether lbs or kg)
      if (isStrength || isStatic) return String(roundMaybe(value, 1));
      if (isSport) return String(roundMaybe(value, 0));
      if (isCardio) return formatCardioDistance(value, distanceUnit);
      return String(roundMaybe(value, 1));
    };

    // const volumePointCount = volumeSeries.filter((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1])).length;

    // Count prior (non-current) volume datapoints with non-zero volume.
    // Use workout id/date matching (not currentPoint), because currentPoint can be null
    // early in a workout (or if history rows are missing ids), which would otherwise
    // cause the current datapoint to be miscounted as "history".
    const currentWorkoutIdStr = currentWorkoutId ? String(currentWorkoutId) : null;
    const currentPointX = (currentPoint && typeof currentPoint.t === 'number' && Number.isFinite(currentPoint.t))
      ? currentPoint.t
      : null;
    const isCurrentWorkoutPoint = (p) => isCurrentWorkoutHistoryPoint(p, {
      currentWorkoutId: currentWorkoutIdStr,
      currentWorkoutDate,
      currentPointX,
    });

    const currentMatchedInPoints = (Array.isArray(points) ? points : []).some((p) => isCurrentWorkoutPoint(p));

    // Also calculate using ALL points to decide if we should show rewards even if history is off-screen.
    // Use raw allPoints from historyStats to avoid potential variable shadowing/scope issues.
    const allKeyPoints = Array.isArray(historyStats?.allPoints) ? historyStats.allPoints : [];
    const globalPriorPositiveVolumeCount = allKeyPoints.filter((p) => {
       const isCurrent = isCurrentWorkoutPoint(p);
       if (isCurrent) return false;
       const raw = (isStrength || categoryLower === 'static')
        ? p?.totalVolume
        : (categoryLower === 'sport')
          ? p?.totalEquivalentVolume
          : null;
      return typeof raw === 'number' && Number.isFinite(raw) && raw > 0;
    }).length;

    const totalPositiveVolumePointCount = volumeSeries.filter((d) => {
      const y = d.value[1];
      return typeof y === 'number' && Number.isFinite(y) && y > 0;
    }).length;
    
    // Calculate point counts based on VISIBLE points for layout logic
    const visibleTimestamps = new Set(points.map(p => p.t));
    const visibleVolumePointCount = volumeSeries
      .filter(d => visibleTimestamps.has(d.value[0]))
      .filter((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1])).length;
    const visibleLeftPointCount = leftSeries
      .filter(d => visibleTimestamps.has(d.value[0]))
      .filter((d) => typeof d.value[1] === 'number' && Number.isFinite(d.value[1])).length;

    const pointCount = Math.max(visibleVolumePointCount, visibleLeftPointCount);
    const totalPointCount = pointCount + (projectedVolumePoint ? 1 : 0);

    const isSinglePoint = totalPointCount === 1;
    const singlePointPadMs = 1000 * 60 * 60 * 12;

    // Give the first/last datapoints some breathing room.
    const rangePadMs = (() => {
      if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin === xMax) return singlePointPadMs;
      const range = Math.max(0, xMax - xMin);
      // 6% of range, with a sensible minimum.
      return Math.max(Math.round(range * 0.06), 1000 * 60 * 60 * 12);
    })();

    const xAxisMin = Number.isFinite(xMin)
      ? (isSinglePoint ? xMin - singlePointPadMs : xMin - rangePadMs)
      : null;
    const xAxisMax = Number.isFinite(xMax)
      ? (isSinglePoint ? xMax + singlePointPadMs : xMax + rangePadMs)
      : null;

    const currentVolumeAxisLabelSeries = (() => {
      if (!hasVolume) return null;
      if (!(typeof currentVolumeY === 'number' && Number.isFinite(currentVolumeY))) return null;
      if (!(currentVolumeY > 0)) return null;
      if (!(typeof xAxisMax === 'number' && Number.isFinite(xAxisMax))) return null;

      const overlapWithMax = (!suppressMaxVolumeLabel) && (labelVolumeValue > 0) && Math.abs(currentVolumeY - labelVolumeValue) < Math.max(1e-6, labelVolumeValue * 0.02);

      return {
        id: 'current-volume-axis-label',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        yAxisIndex: 1,
        data: [[xAxisMax, currentVolumeY]],
        symbolSize: 0,
        silent: true,
        tooltip: { show: false },
        clip: false,
        label: {
          show: true,
          color: 'rgba(255,255,255,0.92)',
          fontSize: 13,
          fontWeight: 600,
          position: 'right',
          distance: 6,
          offset: [0, overlapWithMax ? 14 : 0],
          formatter: () => {
            const toShow = (typeof displayedCurrentVolume === 'number' && Number.isFinite(displayedCurrentVolume))
              ? displayedCurrentVolume
              : currentVolumeY;
            if (!(toShow > 0)) return '';
            return formatVolumeValue(toShow);
          }
        },
        z: 96
      };
    })();

    const showSinglePointSymbols = pointCount === 1;

    // Also show symbols on the volume line when only 1 real volume datapoint is visible.
    // Without this, a single history volume point is invisible because ECharts needs 2+
    // points to draw a line, and showSymbol only applies when there's visible geometry.
    const showVolumeSymbols = showSinglePointSymbols || visibleVolumePointCount <= 1;

    // Only award/paint record cues if there is something to beat.
    // Require at least one prior (non-current) non-zero volume datapoint.
    // Use the GLOBAL history count (globalPriorPositiveVolumeCount) instead of the visible window count
    // so that rewards appear even if the previous history is off-screen.
    const hasPriorVolumeHistory = currentMatchedInPoints
      ? globalPriorPositiveVolumeCount > 0
      : (totalPositiveVolumePointCount > 1 && globalPriorPositiveVolumeCount > 0);

    const hasAnyRecord = Array.isArray(volumeRecordMarkers) && volumeRecordMarkers.length > 0;

    const projectedWouldBeRecord = (() => {
      if (!projectedVolumePoint) return false;
      const y = projectedVolumePoint.y;
      if (!(typeof y === 'number' && Number.isFinite(y))) return false;
      const baseline = maxVolume > 0 ? maxVolume : 0;
      const eps = Math.max(1e-6, Math.abs(baseline) * 1e-6);
      return y > baseline + eps;
    })();

    const projectedVolumeSeries = (() => {
      const base = {
        id: 'projected-volume-marker',
        type: 'scatter',
        name: 'Planned',
        yAxisIndex: 1,
        data: [],
        symbol: 'circle',
        symbolSize: 11,
        silent: !projectedVolumePoint,
        tooltip: { show: Boolean(projectedVolumePoint) },
        emphasis: { focus: 'none' },
        itemStyle: {
          color: 'rgba(250,204,21,0.22)',
          borderColor: 'rgba(250,204,21,0.7)',
          borderWidth: 1.5
        },
        z: 55
      };

      if (!projectedVolumePoint) {
        return base;
      }

      return {
        ...base,
        data: [[projectedVolumePoint.x, projectedVolumePoint.y]]
      };
    })();

    const projectedVolumeCrownSeries = (() => {
      const base = {
        id: 'projected-volume-crown',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        yAxisIndex: 1,
        data: [],
        silent: true,
        tooltip: { show: false },
        clip: false,
        symbol: 'path://M2 16 L2 7 L6 11 L10 5 L14 11 L18 7 L18 16 Z',
        symbolKeepAspect: true,
        symbolSize: 14,
        symbolOffset: [0, -14],
        itemStyle: {
          color: 'rgba(250,204,21,0.55)',
          borderColor: 'rgba(250,204,21,0.9)',
          borderWidth: 1
        },
        z: 93
      };

      if (!(projectedVolumePoint && projectedWouldBeRecord && hasPriorVolumeHistory)) {
        return base;
      }

      return {
        ...base,
        data: [[projectedVolumePoint.x, projectedVolumePoint.y]]
      };
    })();

    const crownTargetsCurrentVolume = (() => {
      if (!hasPriorVolumeHistory) return false;
      if (!hasAnyRecord) return false;
      if (!maxVolumePoint || !currentVolumeMarker) return false;
      const dx = Math.abs(maxVolumePoint.x - currentVolumeMarker.x);
      const dy = Math.abs(maxVolumePoint.y - currentVolumeMarker.y);
      return dx < 1 && dy < 1e-6;
    })();

    const volumeRecordSeries = (() => {
      const base = {
        id: 'volume-record-markers',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        yAxisIndex: 1,
        data: [],
        silent: true,
        tooltip: { show: false },
        clip: false,
        symbol: 'path://M0 10 L6 2 L12 10 L9.5 10 L6 5.5 L2.5 10 Z',
        symbolKeepAspect: true,
        symbolSize: 11,
        itemStyle: {
          color: 'rgba(250,204,21,0.85)',
          shadowBlur: 8,
          shadowColor: 'rgba(0,0,0,0.35)'
        },
        z: 85
      };

      if (!hasPriorVolumeHistory) return base;
      const markers = Array.isArray(volumeRecordMarkers) ? volumeRecordMarkers : [];
      if (!markers.length) return base;
      const filteredMarkers = (() => {
        if (!crownPoint) return markers;
        const maxX = crownPoint.x;
        const maxY = crownPoint.y;
        if (!(typeof maxX === 'number' && Number.isFinite(maxX))) return markers;
        if (!(typeof maxY === 'number' && Number.isFinite(maxY))) return markers;
        return markers.filter((point) => {
          const x = point?.x;
          const y = point?.y;
          if (!(typeof x === 'number' && Number.isFinite(x))) return false;
          if (!(typeof y === 'number' && Number.isFinite(y))) return false;
          const sameX = Math.abs(x - maxX) < 1e-6;
          const sameY = Math.abs(y - maxY) < 1e-6;
          return !(sameX && sameY);
        });
      })();
      if (!filteredMarkers.length) return base;
      return {
        ...base,
        data: filteredMarkers.map((point) => {
          const isCurrent = (() => {
            if (!currentVolumeMarker) return false;
            const dx = Math.abs(point.x - currentVolumeMarker.x);
            const dy = Math.abs(point.y - currentVolumeMarker.y);
            return dx < 1 && dy < 1e-6;
          })();

          if (isCurrent) {
            const pulseSize = (volumeBumpPulseTick % 2 === 0) ? 16 : 19;
            return {
              value: [point.x, point.y],
              id: `record-${point.x}`,
              groupId: `record-${point.x}`,
              symbolSize: pulseSize,
              symbolOffset: [0, -1 * (pulseSize + 2)],
              itemStyle: {
                color: SCORE_COLOR,
                borderColor: 'rgba(255,255,255,0.9)',
                borderWidth: 1.5,
                shadowBlur: 10,
                shadowColor: 'rgba(250,204,21,0.6)'
              },
              z: 110
            };
          }

          return {
            value: [point.x, point.y],
            id: `record-${point.x}`,
            groupId: `record-${point.x}`,
            symbolOffset: [0, -12]
          };
        })
      };
    })();

    // Render date labels aligned with the first/last datapoints.
    // We keep x-axis padded for breathing room, so axis min/max ticks won't match datapoint x.
    const xEndpointLabelSeries = (() => {
      const minT = Number.isFinite(xMin) ? xMin : null;
      const maxT = Number.isFinite(xMax) ? xMax : null;
      if (minT === null || maxT === null) return null;

      const endpoints = (minT === maxT) ? [minT] : [minT, maxT];
      const y = leftScale?.min ?? 0;

      return {
        id: 'x-endpoint-labels',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        yAxisIndex: 0,
        data: endpoints.map((t) => [t, y]),
        symbolSize: 0,
        silent: true,
        tooltip: { show: false },
        clip: false,
        label: {
          show: true,
          color: 'rgba(255,255,255,0.65)',
          fontSize: 13,
          position: 'bottom',
          distance: 8,
          formatter: (params) => {
            const value = Array.isArray(params?.value) ? params.value[0] : params?.value;
            return formatDateLabel(value);
          }
        },
        z: 90
      };
    })();

    // Built at rest opacity; the pulse driver patches this series in place per frame.
    const leftSeriesWithCurrentBorder = hasLeft
      ? withPulsedCurrentBar(leftSeries, currentPoint, 1)
      : leftSeries;

    // Keep overlays always present so they follow the datapoint as it animates.
    const volumeBumpPulse = (currentVolumeMarker && hasPriorVolumeHistory)
      ? {
          id: 'volume-bump-pulse',
          type: 'effectScatter',
          coordinateSystem: 'cartesian2d',
          yAxisIndex: 1,
          data: [[currentVolumeMarker.x, currentVolumeMarker.y]],
          silent: true,
          tooltip: { show: false },
          universalTransition: true,
          symbol: 'circle',
          // Use size to toggle the effect on/off; opacity alone doesn't reliably
          // affect ripple visibility across renderers.
          symbolSize: showVolumeBump ? 10 : 0,
          itemStyle: {
            // IMPORTANT: ripple color defaults to itemStyle.color, so keep this non-transparent.
            color: 'rgba(250,204,21,0.75)',
            borderColor: 'rgba(250,204,21,0.95)',
            borderWidth: 2,
            shadowBlur: 18,
            shadowColor: 'rgba(250,204,21,0.55)'
          },
          rippleEffect: {
            // Gentle pulse; visible window is controlled by showVolumeBump.
            // Keep short so it's visible within UPDATE_ANIMATION_MS.
            period: 0.32,
            scale: 2.2,
            brushType: 'stroke',
            color: 'rgba(250,204,21,0.85)',
            number: 2
          },
          z: 60
        }
      : null;

    const volumeBumpGlow = (currentVolumeMarker && hasPriorVolumeHistory)
      ? {
          id: 'volume-bump-glow',
          type: 'scatter',
          coordinateSystem: 'cartesian2d',
          yAxisIndex: 1,
          data: [[currentVolumeMarker.x, currentVolumeMarker.y]],
          silent: true,
          tooltip: { show: false },
          universalTransition: true,
          symbol: 'circle',
          // One-shot "pop" by animating size down to 0 when showVolumeBump flips off.
          symbolSize: showVolumeBump ? 22 : 0,
          itemStyle: {
            color: 'rgba(250,204,21,0.18)',
            borderColor: 'rgba(250,204,21,0.95)',
            borderWidth: 2,
            shadowBlur: 30,
            shadowColor: 'rgba(250,204,21,0.75)'
          },
          z: 70
        }
      : null;

    const volumeBumpArrows = (currentVolumeMarker && hasPriorVolumeHistory)
      ? {
          id: 'volume-bump-arrows',
          type: 'scatter',
          coordinateSystem: 'cartesian2d',
          yAxisIndex: 1,
          data: [
            {
              value: [currentVolumeMarker.x, currentVolumeMarker.y],
              symbolOffset: [0, -14]
            },
            {
              value: [currentVolumeMarker.x, currentVolumeMarker.y],
              symbolOffset: [0, -22]
            }
          ],
          silent: true,
          tooltip: { show: false },
          universalTransition: true,
          // Short chevron-up.
          symbol: 'path://M0 8 L8 0 L16 8 L12.5 8 L8 3.5 L3.5 8 Z',
          symbolKeepAspect: true,
          symbolSize: (showVolumeBump && !crownTargetsCurrentVolume)
            ? ((volumeBumpPulseTick % 2 === 0) ? 10 : 12)
            : 0,
          itemStyle: {
            color: 'rgba(255,255,255,0.92)',
            shadowBlur: 18,
            shadowColor: 'rgba(250,204,21,0.55)'
          },
          z: 75
        }
      : null;

    // Special cue for the biggest volume datapoint.
    const maxVolumeCrownSeries = (() => {
      // Always emit the series with a stable id so ECharts removes stale crown points.
      const base = {
        id: 'max-volume-crown',
        type: 'scatter',
        coordinateSystem: 'cartesian2d',
        yAxisIndex: 1,
        data: [],
        silent: true,
        tooltip: { show: false },
        clip: false,
        symbol:
          'path://M2 16 L2 7 L6 11 L10 5 L14 11 L18 7 L18 16 Z',
        symbolKeepAspect: true,
        symbolSize: 16,
        symbolOffset: [0, -16],
        itemStyle: {
          // Always keep the crown yellow.
          color: SCORE_COLOR,
          borderColor: 'rgba(250,204,21,0.95)',
          borderWidth: 1
        },
        z: 95
      };

      const shouldShow = (() => {
        if (!hasPriorVolumeHistory) return false;
        const p = crownPoint;
        if (!p) return false;
        if (!(typeof p.x === 'number' && Number.isFinite(p.x))) return false;
        if (!(typeof p.y === 'number' && Number.isFinite(p.y))) return false;
        if (!(p.y > 0)) return false;
        if (!hasVolume) return false;
        return true;
      })();

      if (!shouldShow) {
        return base;
      }

      // If the crown is on the current datapoint, make it pulsate and glow to celebrate.
      if (crownTargetsCurrentVolume) {
        const pulseSize = (volumeBumpPulseTick % 2 === 0) ? 22 : 26;
        return {
          ...base,
          data: [[crownPoint.x, crownPoint.y]],
          symbolSize: pulseSize,
          symbolOffset: [0, -1 * pulseSize],
          itemStyle: {
            color: SCORE_COLOR,
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 15,
            shadowColor: 'rgba(255,255,255,0.75)'
          },
          z: 110
        };
      }

      return {
        ...base,
        data: [[crownPoint.x, crownPoint.y]]
      };
    })();

    return {
      animation: true,
      animationThreshold: 5000,
      animationDuration: 450,
      animationEasing: 'cubicOut',
      animationDurationUpdate: UPDATE_ANIMATION_MS,
      animationEasingUpdate: 'cubicOut',
      grid: {
        left: 36,
        right: 46,
        top: 8,
        bottom: 34,
        containLabel: false
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderColor: 'rgba(255, 255, 255, 0.0)',
        textStyle: {
          color: '#fff',
          fontSize: 14
        },
        extraCssText: 'backdrop-filter: blur(0px); border-radius: 4px; padding: 4px 8px;',
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(255,255,255,0.18)', width: 1 }
        },
        formatter: (params) => {
          const items = Array.isArray(params) ? params : [params];
          const first = items[0];
          const dateLabel = first?.axisValue ? formatDateLabel(first.axisValue) : '';

          const lines = [];
          if (dateLabel) {
            lines.push(`<div style="font-weight:600; margin-bottom:2px;">${dateLabel}</div>`);
          }

          items.forEach((it) => {
            // Don't show the current-point marker in the tooltip.
            if (it?.seriesId === 'current-marker' || it?.seriesName === 'Current') {
              return;
            }
            const seriesName = it?.seriesName || '';
            const color = it?.color || '#fff';
            const value = Array.isArray(it?.value) ? it.value[1] : it?.value;

            let displayValue = '-';
            if (seriesName === volumeName || seriesName === scoreName) {
              displayValue = formatVolumeValue(value);
            } else {
              displayValue = formatLeftValue(value);
            }

            lines.push(
              `<div style="display:flex; gap:6px; align-items:baseline;">` +
              `<span style="display:inline-block; width:8px; height:8px; border-radius:999px; background:${color}; margin-top:2px;"></span>` +
              `<span style="opacity:0.9;">${seriesName}:</span>` +
              `<span style="font-weight:600;">${displayValue}</span>` +
              `</div>`
            );
          });

          return lines.join('');
        }
      },
      xAxis: {
        type: 'value',
        min: xAxisMin,
        max: xAxisMax,
        interval: isSinglePoint ? singlePointPadMs : undefined,
        splitNumber: isSinglePoint ? undefined : 2,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: 'rgba(255,255,255,0.06)', width: 1 }
        },
        axisLabel: { show: false }
      },
      yAxis: [
        {
          type: 'value',
          position: 'left',
          name: '',
          min: leftScale.min,
          max: leftScale.max,
          interval: leftScale.interval,
          splitNumber: leftScale.splitNumber,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: { color: 'rgba(255,255,255,0.06)', width: 1 }
          },
          axisLabel: {
            color: leftSeriesColor,
            formatter: (value) => {
              // Only show one label: the maximum data value.
              const v = Number(value);
              if (!Number.isFinite(v)) return '';
              if (Math.abs(v - leftScale.labelValue) > Math.max(1e-6, leftScale.labelValue * 1e-6)) return '';

              const toShowRaw = (typeof displayedMaxLeft === 'number' && Number.isFinite(displayedMaxLeft))
                ? displayedMaxLeft
                : leftScale.labelValue;

              if (!(toShowRaw > 0)) return '';

              if (isStretching) return formatDurationCompact(Math.round(toShowRaw));
              // Always show 1 decimal max for weight values
              if (isStrength || isStatic) return String(roundMaybe(toShowRaw, 1));
              if (isSport) return String(roundMaybe(toShowRaw, 0));
              if (isCardio) return String(roundMaybe(toShowRaw, 2));
              return String(roundMaybe(toShowRaw, 1));
            }
          }
        },
        {
          type: 'value',
          position: 'right',
          name: '',
          min: 0,
          max: volumeAxisMax,
          // Ensure we get a tick exactly at the max datapoint value (real or projected).
          interval: Math.max(labelVolumeValue, 1),
          splitNumber: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: {
            // Cardio already draws the distance grid from the left axis. A
            // second, independently scaled duration grid makes Hike charts
            // look split and much denser than other exercise charts.
            show: !isCardio,
            lineStyle: { color: 'rgba(255,255,255,0.06)', width: 1 }
          },
          axisLabel: {
            color: isCardio ? VOLUME_COLOR : SCORE_COLOR,
            formatter: (value) => {
              const v = Number(value);
              if (!Number.isFinite(v)) return '';
              if (!(labelVolumeValue > 0)) return '';
              if (suppressMaxVolumeLabel) return '';
              // Only show one label, aligned to the max datapoint.
              if (Math.abs(v - labelVolumeValue) > Math.max(1e-6, labelVolumeValue * 1e-6)) return '';
              const toShow = isCardio
                ? labelVolumeValue
                : ((typeof displayedMaxVolume === 'number' && Number.isFinite(displayedMaxVolume))
                  ? displayedMaxVolume
                  : Math.round(labelVolumeValue));

              if (!(toShow > 0)) return '';
              if (isCardio) return formatDurationCompact(toShow * 60);
              return String(roundMaybe(toShow, 0));
            }
          }
        }
      ],
      series: [
        ...(hasVolume
          ? [
              // Volume line removed for strength/static/sport workouts: the
              // score curve already conveys the trend on the right axis, and
              // the volume line was visually squashed at the bottom because
              // its scale dwarfs everything else. Cardio still keeps it as
              // its only right-axis line (duration in minutes).
              ...(((isStrength || isStatic || isSport) && scoreSeries.length)
                ? []
                : [
                    {
                      id: 'volume',
                      type: 'line',
                      name: volumeName,
                      yAxisIndex: 1,
                      data: volumeSeries,
                      universalTransition: true,
                      smooth: true,
                      showSymbol: showVolumeSymbols,
                      symbol: 'circle',
                      symbolSize: SELECTION_SYMBOL_SIZE_PX,
                      itemStyle: { color: VOLUME_COLOR },
                      lineStyle: { width: 2, color: VOLUME_COLOR },
                      emphasis: { focus: 'none' },
                      blur: {
                        lineStyle: { opacity: 1 },
                        itemStyle: { opacity: 1 }
                      }
                    }
                  ]),
              ...((isStrength || isStatic || isSport) && scoreSeries.length
                ? [
                    {
                      id: 'score',
                      type: 'line',
                      name: scoreName,
                      yAxisIndex: 1,
                      data: scoreSeries,
                      universalTransition: true,
                      smooth: true,
                      showSymbol: showVolumeSymbols,
                      symbol: 'circle',
                      symbolSize: SELECTION_SYMBOL_SIZE_PX,
                      itemStyle: { color: SCORE_COLOR },
                      lineStyle: { width: 2, color: SCORE_COLOR },
                      emphasis: { focus: 'none' },
                      blur: {
                        lineStyle: { opacity: 1 },
                        itemStyle: { opacity: 1 }
                      }
                    }
                  ]
                : [])
            ]
          : []),
        // Always include the bar series (even with empty/null data) so ECharts
        // transitions data within a stable series instead of add/remove, which
        // causes animation glitches with notMerge=false.
        {
          id: 'left',
          type: 'bar',
          name: leftName,
          yAxisIndex: 0,
          data: leftSeriesWithCurrentBorder,
          universalTransition: true,
          // Allow ECharts to auto-size bar width based on density.
          barMinWidth: 2,
          barMaxWidth: SELECTION_SYMBOL_SIZE_PX,
          itemStyle: { color: leftSeriesColor },
          emphasis: { focus: 'none' },
          blur: {
            itemStyle: { opacity: 1 }
          }
        }
        ,
        ...(projectedVolumeSeries ? [projectedVolumeSeries] : []),
        currentMarkerSeries ? {
          ...currentMarkerSeries,
          itemStyle: pulsedMarkerItemStyle(currentMarkerSeries.itemStyle, 1)
        } : null,
        ...(volumeBumpPulse ? [volumeBumpPulse] : []),
        ...(volumeBumpGlow ? [volumeBumpGlow] : []),
        ...(volumeBumpArrows ? [volumeBumpArrows] : []),
        ...(volumeRecordSeries ? [volumeRecordSeries] : []),
        ...(projectedVolumeCrownSeries ? [projectedVolumeCrownSeries] : []),
        ...(xEndpointLabelSeries ? [xEndpointLabelSeries] : []),
        // Always include the label series placeholder so ECharts clears it if data becomes null
        (currentVolumeAxisLabelSeries ? currentVolumeAxisLabelSeries : {
           id: 'current-volume-axis-label',
           type: 'scatter',
           data: []
        }),
        ...(maxVolumeCrownSeries ? [maxVolumeCrownSeries] : [])
      ]
    };
  }, [computed, weightUnit, distanceUnit, showVolumeBump, volumeBumpPulseTick, displayedMaxVolume, displayedCurrentVolume, displayedMaxLeft, completedVolume, currentWorkoutDate, currentWorkoutId, historyStats.allPoints]);

  if (isLoading || !isRangeValid) {
    return (
      <div className="exercise-history-chart">
        <div className="exercise-history-chart-body">
          <div className="exercise-history-loading-large">Loading…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="exercise-history-chart">
        <div className="exercise-history-chart-body">
          <div className="exercise-history-chart-empty-message">Unable to load history.</div>
        </div>
      </div>
    );
  }

  if (!option) {
    return (
      <div className="exercise-history-chart">
        {computed?.hasAnyHistory && computed?.totalHistoryDuration > RANGES.MONTH && (
          <div className="exercise-history-controls">
            <button className={selectedRange === RANGES.MONTH ? 'active' : ''} onClick={() => setSelectedRange(RANGES.MONTH)}>1M</button>
            {computed?.totalHistoryDuration > RANGES.MONTH && (
              <button className={selectedRange === RANGES.THREE_MONTHS ? 'active' : ''} onClick={() => setSelectedRange(RANGES.THREE_MONTHS)}>3M</button>
            )}
            {computed?.totalHistoryDuration > RANGES.THREE_MONTHS && (
              <button className={selectedRange === RANGES.YEAR ? 'active' : ''} onClick={() => setSelectedRange(RANGES.YEAR)}>1Y</button>
            )}
            {computed?.totalHistoryDuration > RANGES.YEAR && (
              <button className={selectedRange === RANGES.ALL ? 'active' : ''} onClick={() => setSelectedRange(RANGES.ALL)}>All</button>
            )}
          </div>
        )}
        <div className="exercise-history-chart-body">
          <div className="exercise-history-chart-empty-message">No history yet.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="exercise-history-chart">
      {computed?.hasAnyHistory && computed?.totalHistoryDuration > RANGES.MONTH && (
        <div className="exercise-history-controls">
          <button className={selectedRange === RANGES.MONTH ? 'active' : ''} onClick={() => setSelectedRange(RANGES.MONTH)}>1M</button>
          {computed?.totalHistoryDuration > RANGES.MONTH && (
            <button className={selectedRange === RANGES.THREE_MONTHS ? 'active' : ''} onClick={() => setSelectedRange(RANGES.THREE_MONTHS)}>3M</button>
          )}
          {computed?.totalHistoryDuration > RANGES.THREE_MONTHS && (
            <button className={selectedRange === RANGES.YEAR ? 'active' : ''} onClick={() => setSelectedRange(RANGES.YEAR)}>1Y</button>
          )}
          {computed?.totalHistoryDuration > RANGES.YEAR && (
            <button className={selectedRange === RANGES.ALL ? 'active' : ''} onClick={() => setSelectedRange(RANGES.ALL)}>All</button>
          )}
        </div>
      )}
      <div className="exercise-history-chart-body">
        <ReactECharts
          key={workoutExerciseId}
          ref={chartRef}
          option={option}
          notMerge={false}
          lazyUpdate={true}
          autoResize={true}
          style={{
            height: '160px',
            width: '100%'
          }}
        />
      </div>
    </div>
  );
}

export default WorkoutExerciseHistoryChart;
